import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Form, Input, Select, Typography } from 'antd'
import Const from 'constants/constant'
import ViewerFilePreview from 'pages/building/buildingDetailViewMode/components/ViewerFilePreview'
import Quill from 'quill'
import CustomBlotFormatter from 'quill/CustomBlotFormatter'
import { Alert, AlertDraftUser } from 'types/alert'
import { AnnotationType, BuildingFile } from 'types/building'

import Button from 'components/atoms/Button'
import Modal from 'components/atoms/Modal'
import Title from 'components/atoms/Title'
import DropdownIconCustom from 'components/icons/DropdownIconCustom'
import LeftArrowIcon from 'components/icons/LeftArrowIcon'
import RightArrowIcon from 'components/icons/RightArrowIcon'

import ModalAlertSetting from '../ModalAlertSetting/ModalAlertSetting'

import useBcpAnnotation from './hooks/useBcpAnnotation'
import BCPAnnotationCategoryDetail from './BCPAnnotationCategoryDetail'
import BCPConfirmChangeAnnotation from './BCPConfirmChangeAnnotation'
import BCPDeleteAnnotationModal from './BCPDeleteAnnotationModal'
import BCPMemoSetting from './BCPMemoSetting'

// Register the blot formatter module
if (!Quill.imports['modules/blotFormatter']) {
  Quill.register('modules/blotFormatter', CustomBlotFormatter)
}

export default function BCPAnnotationModal() {
  const { id: buildingId, floorId } = useParams()
  const {
    t,
    selectedType,
    changeSelectedType,
    annotationList,
    currentSelected,
    setMemoContent,
    handleChangeTab,
    setSelectedCategory,
    form,
    selectedTabs,
    selectedTabIndex,
    linkParse,
    nameParse,
    formHandover,
    getHandoverNameField,
    getHandoverNameValue,
    handleHandoverNameChange,
    quillRefHandover,
    memoName,
    handleMemoNameChange,
    setDisableWhenMemoNotCompleted,
    annotationBgColor,
    selectedCategory,
    handleCategoryBack,
    listTabs,
    setIsDeleting,
    handleCloseModal,
    isLoading,
    handleAddOrEditAnnotation,
    disableWhenMemoNotCompleted,
    hasHandoverError,
    hasLinkError,
    setHasLinkError,
    isDeleting,
    isChangeType,
    nextType,
    setSelectedType,
    setIsChangeType,
    setCurrentSelected,
    previewFileIndex,
    setPreviewFileIndex,
    tempAlertData,
    setTempAlertData,
    isFetchingContent
  } = useBcpAnnotation()

  const [isOpenAlertSetting, setIsOpenAlertSetting] = useState(false)
  const [existsFile, setExistsFile] = useState<BuildingFile[]>([])

  const alertTextColor =
    currentSelected?.alert?.id || tempAlertData
      ? 'text-[#3451D1]'
      : 'text-[#E73A3A]'

  const initialAlertData = useMemo(() => {
    if (!tempAlertData) return undefined

    const repeatOption: 'repeat' | 'none' =
      tempAlertData.repeat_type === 2 ? 'repeat' : 'none'

    let repeatType: 'weekly' | 'monthly' | 'yearly' = 'weekly'
    if (tempAlertData.repeat_unit_type === 2) repeatType = 'monthly'
    if (tempAlertData.repeat_unit_type === 3) repeatType = 'yearly'

    let endType: 'never' | 'on_date' | 'after_occurrences' = 'never'
    if (tempAlertData.end_type === 2) endType = 'on_date'
    if (tempAlertData.end_type === 3) endType = 'after_occurrences'

    return {
      title: tempAlertData.title,
      alertDateTime: tempAlertData.send_at,
      users: (tempAlertData.extra_users_data || []).map(
        (u: AlertDraftUser) => ({
          userId: u.userId,
          email: u.email,
          lastName: u.lastName,
          firstName: u.firstName,
          organizationName: u.organizationName,
          permission: u.permission
        })
      ),
      repeatSchedule: {
        option: repeatOption,
        interval: tempAlertData.repeat_unit,
        type: repeatType,
        weekDays: tempAlertData.repeat_week_days,
        endType,
        endDate: tempAlertData.end_date ?? undefined,
        occurrences: tempAlertData.end_count ?? undefined
      },
      content: tempAlertData.content,
      body_content: tempAlertData.body_content
    } as Partial<Alert>
  }, [tempAlertData])

  return (
    <>
      <Modal
        width={1200}
        className="paddingless-modal"
        destroyOnClose
        onCancel={handleCloseModal}
      >
        <Form
          form={form}
          colon={false}
          labelCol={{ span: 5 }}
          labelAlign="left"
          wrapperCol={{ span: 19 }}
          className="pt-10 px-[60px] pb-10"
          onFieldsChange={(_, allFields) => {
            const hasError = allFields.some(
              (f) =>
                f.name &&
                Array.isArray(f.name) &&
                f.name[0] === 'linksStage' &&
                f.errors &&
                f.errors.length > 0
            )
            setHasLinkError(hasError)
          }}
        >
          <Title title={t('building.titleAddAnnotation')} />
          <div className="flex items-center gap-[5.5rem] -mt-5 w-full relative z-10">
            <Typography className="text-[#021120] text-[14px] leading-[1] font-bold">
              {t('building.annotationType')}
            </Typography>
            <Select
              value={selectedType}
              onSelect={changeSelectedType}
              options={annotationList}
              suffixIcon={<DropdownIconCustom colorIcon="#000000" />}
              style={{
                width: 240,
                fontSize: 14,
                color: '#021120'
              }}
            />
            <div className="flex flex-col items-end justify-center absolute right-[0] -top-1 gap-1">
              <span
                className={`text-[10px] leading-3 font-bold ${alertTextColor}`}
              >
                {currentSelected?.alert?.id || tempAlertData
                  ? '設定中'
                  : '未設定'}
              </span>
              <div
                className="flex items-center justify-center border border-solid border-[#3451D1] text-[#3451D1] rounded cursor-pointer px-4 h-[30px] text-[12px] font-bold bg-white"
                onClick={() => setIsOpenAlertSetting(true)}
              >
                アラート設定
              </div>
            </div>
          </div>

          <div className="relative" style={{ minHeight: 400 }}>
            <div
              className={
                selectedType === AnnotationType.MEMO ? 'visible' : 'invisible'
              }
              style={{ position: 'absolute', top: 0, left: 0, width: '100%' }}
            >
              <BCPMemoSetting
                memo={currentSelected?.memo}
                memoName={memoName}
                onMemoNameChange={handleMemoNameChange}
                onMemoContentChange={setMemoContent}
                setDisableWhenMemoNotCompleted={setDisableWhenMemoNotCompleted}
              />
            </div>
            <div
              className={
                selectedType === AnnotationType.MEMO ? 'invisible' : 'visible'
              }
              style={{ position: 'absolute', top: 0, left: 0, width: '100%' }}
            >
              <div
                className={`flex items-center ${annotationBgColor} mt-[10px]`}
              >
                {selectedCategory ? (
                  <div
                    className={`h-6 ml-5 ${
                      hasLinkError && selectedCategory === 'link'
                        ? 'cursor-not-allowed opacity-50'
                        : 'cursor-pointer'
                    }`}
                    onClick={() => {
                      if (hasLinkError && selectedCategory === 'link') return
                      handleCategoryBack()
                    }}
                  >
                    <LeftArrowIcon size={24} />
                  </div>
                ) : null}
                <div className="flex relative">
                  {listTabs.map((item) => (
                    <div
                      key={item.value}
                      className="flex w-20 h-[50px] cursor-pointer"
                      onClick={() => handleChangeTab(item.value)}
                    >
                      <div className="text-base text-white m-auto">
                        {t(item.label)}
                      </div>
                    </div>
                  ))}
                  <div
                    className="absolute bottom-0 transition-all w-20 h-[5px] bg-[#A1C6B4]"
                    style={{
                      left: `${selectedTabIndex * 80}px`
                    }}
                  />
                </div>
              </div>
              <div className="relative">
                <div className={selectedCategory ? 'hidden z-0' : 'z-10'}>
                  {Const.BCP_ANNOTATION_TAB_CATEGORIES.map((item) => (
                    <div
                      key={item.value}
                      className="flex items-center justify-between h-[60px] border-0 border-b border-solid border-[#d9d9d9] cursor-pointer"
                      onClick={() => setSelectedCategory(item.value)}
                    >
                      <div className="text-base text-[#021120] font-medium ml-[10px]">
                        {t(item.label)}
                      </div>
                      <div className="mr-5">
                        <RightArrowIcon color="#021120" />
                      </div>
                    </div>
                  ))}
                </div>

                <div
                  className={
                    selectedCategory ? 'opacity-100' : 'opacity-0 hidden'
                  }
                >
                  {selectedCategory !== 'handover' && (
                    <BCPAnnotationCategoryDetail
                      form={form}
                      selectedType={selectedType}
                      selectedTabs={selectedTabs}
                      selectedTabIndex={selectedTabIndex}
                      selectedCategory={selectedCategory}
                      linkParse={linkParse}
                      nameParse={nameParse}
                      annotationBgColor={annotationBgColor}
                      setExistsFile={setExistsFile}
                      setPreviewFileIndex={setPreviewFileIndex}
                      listTabs={listTabs}
                    />
                  )}

                  <div
                    className={`absolute top-0 left-0 w-full h-full ${
                      selectedCategory === 'handover'
                        ? 'opacity-100 z-[999]'
                        : 'opacity-0 z-[-1]'
                    }`}
                  >
                    <Form component={false} form={formHandover}>
                      <div className="pt-3 ml-14">
                        <Typography className="text-[#021120] text-[14px] leading-[1]">
                          申し送り名
                        </Typography>
                        <Form.Item
                          name={getHandoverNameField(selectedTabIndex)}
                          className="!mb-0 flex-1 !w-1/2 !min-h-[72px] pt-2"
                          rules={[
                            {
                              required: true,
                              message: '申し送り名を入力してください'
                            },
                            {
                              min: 1,
                              max: 100,
                              message: '1〜100文字で入力してください'
                            }
                          ]}
                        >
                          <Input
                            placeholder="入力してください"
                            value={getHandoverNameValue(selectedTabIndex)}
                            onChange={handleHandoverNameChange}
                            className="w-full text-sm"
                          />
                        </Form.Item>

                        <div className="h-[300px] px-0">
                          <div
                            ref={quillRefHandover}
                            className="quill-custom-editor overflow-hidden [&>#editor-resizer>.toolbar]:!hidden [&>.ql-editor]:pb-4"
                          />
                        </div>
                      </div>
                    </Form>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* button */}
          <div className="flex pl-5 pr-[30px] pt-5 border-0 border-t border-solid border-[#EEEFF2] mt-28">
            {currentSelected?.id ? (
              <Button
                danger
                type="primary"
                sizing="w-[140px] h-[50px]"
                onClick={() => setIsDeleting(true)}
              >
                {t('common.delete')}
              </Button>
            ) : null}
            <div className="flex gap-10 ml-auto">
              <Button sizing="w-[140px] h-[50px]" onClick={handleCloseModal}>
                {t('common.cancel')}
              </Button>
              <Button
                sizing="w-[140px] h-[50px]"
                className="theme-bg-primary text-white"
                style={{ color: 'white' }}
                loading={isLoading || isFetchingContent}
                onClick={handleAddOrEditAnnotation}
                disabled={
                  isFetchingContent ||
                  (selectedType === AnnotationType.MEMO &&
                    disableWhenMemoNotCompleted) ||
                  (selectedCategory === 'handover' && hasHandoverError) ||
                  (selectedCategory === 'link' && hasLinkError)
                }
              >
                {t('common.confirm')}
              </Button>
            </div>
          </div>
        </Form>
      </Modal>
      {isDeleting && (
        <BCPDeleteAnnotationModal
          handleCancelDelete={() => setIsDeleting(false)}
          handleCloseModal={handleCloseModal}
        />
      )}
      {isChangeType && nextType !== undefined && (
        <BCPConfirmChangeAnnotation
          selectedType={nextType}
          setSelectedType={setSelectedType}
          setIsOpenConfirm={setIsChangeType}
        />
      )}
      {isOpenAlertSetting && (
        <ModalAlertSetting
          handleCloseModal={() => setIsOpenAlertSetting(false)}
          onSaveSuccess={(res: any) => {
            if (currentSelected) {
              const newAlertId =
                res?.data?.id ||
                res?.id ||
                res?.data?.alert?.id ||
                res?.alert?.id ||
                undefined
              if (newAlertId) {
                setCurrentSelected({
                  ...currentSelected,
                  alert: { id: newAlertId }
                })
              }
            }
            setIsOpenAlertSetting(false)
          }}
          onDeleteSuccess={() => {
            if (currentSelected) {
              setCurrentSelected({ ...currentSelected, alert: undefined })
            }
            setIsOpenAlertSetting(false)
          }}
          buildingId={buildingId}
          floorId={floorId ? Number(floorId) : undefined}
          annotationId={
            currentSelected?.id ? String(currentSelected.id) : undefined
          }
          pointId={currentSelected?.point_id}
          alertId={currentSelected?.alert?.id}
          onSaveDraft={(payload) => {
            setTempAlertData(payload)
            setIsOpenAlertSetting(false)
          }}
          initialData={initialAlertData}
        />
      )}
      {previewFileIndex !== undefined && (
        <ViewerFilePreview
          fileIndex={previewFileIndex}
          file={existsFile[previewFileIndex]}
          maxLength={existsFile.length}
          setPreviewFileIndex={setPreviewFileIndex}
        />
      )}
    </>
  )
}
